import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { isApiErrorResponse } from '../types/api';
import { AUTH_CONFIG } from '../config/auth';
import { authLogger, apiLogger, uiLogger } from '../utils/loggerInstance';
import { refreshToken, triggerAuthError, getCsrfTokenFromRegistry } from './tokenRefreshRegistry';
import { setupSecurityInterceptors } from '../utils/securityInterceptor';
import { performanceMonitor } from '../utils/performanceMonitor';
import { CHAT_ENDPOINTS } from '../constants/apiEndpoints';
import { isUnauthorizedError, isForbiddenError, handleAuthError } from './authErrorHelper';
import { getCsrfTokenFromDocumentCookie } from '../utils/cookieUtils';
import { attachSupportRequestIdToAxiosError } from '../utils/supportErrorUtils';
import { PROFILE_ENTERPRISE_BILLING_PATH } from '../constants/billingProfilePaths';

// Extend AxiosRequestConfig to include retry flag and performance tracking
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _startTime?: number;
  useCache?: boolean;
}

// Extend AxiosInstance to include support for ExtendedAxiosRequestConfig
export interface ExtendedAxiosInstance extends AxiosInstance {
  get<T = any, R = AxiosResponse<T>>(url: string, config?: ExtendedAxiosRequestConfig): Promise<R>;
}

/**
 * Get access token from localStorage (deprecated - tokens now in HTTP-only cookies)
 * Kept for backward compatibility during migration
 * @deprecated Use HTTP-only cookies instead
 */
const getAccessToken = (): string | null => {
  // Tokens are now in HTTP-only cookies, not accessible via JavaScript
  // This function is kept for backward compatibility but returns null
  return null;
};

/**
 * Refresh access token using registered callback (centralized logic)
 * Note: Tokens are now in HTTP-only cookies, so we don't return the token
 */
const refreshAccessToken = async (): Promise<void> => {
  try {
    // Use registered refresh callback to avoid circular dependency
    await refreshToken();
    // Tokens are in HTTP-only cookies, no need to return them
  } catch (error) {
    authLogger.error('Failed to refresh access token via registry:', error);
    throw error;
  }
};

/**
 * Clear service worker cache when authentication errors occur
 * This ensures users don't see stale cached data after token expiration
 */
const clearServiceWorkerCache = (): void => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    authLogger.info('Sent cache clear message to service worker');
  }
};

/**
 * Check if URL matches any of the provided endpoints
 */
const matchesEndpoint = (url: string | undefined, endpoints: string[]): boolean => {
  return url ? endpoints.some((endpoint) => url.startsWith(endpoint)) : false;
};

/**
 * Check if request is to an auth endpoint
 */
const isAuthEndpoint = (url: string | undefined): boolean => {
  return matchesEndpoint(url, [
    '/api/v1/auth/forgot-password',
    '/api/v1/auth/register',
    '/api/v1/auth/login',
    '/api/v1/auth/login/verify-mfa',
    '/api/v1/auth/login/resend-mfa-code',
    '/api/v1/auth/email-verification/send',
    '/api/v1/auth/email-verification/resend',
    '/api/v1/auth/refresh',
    '/api/v1/auth/logout',
    '/api/v1/auth/reset-password/verify',
    '/api/v1/auth/reset-password',
    '/api/v1/auth/verify',
  ]);
};

/**
 * Check if request is to an export endpoint
 */
const isExportEndpoint = (url: string | undefined): boolean => {
  return matchesEndpoint(url, [
    '/api/v1/export/cloud/export',
    '/api/v1/export/cloud/folders',
    '/api/v1/export/forms/export',
  ]);
};

/**
 * Check if the error is a CSRF-related error
 */
const isCsrfError = (error: AxiosError): boolean => {
  if (error.response?.status !== 403) {
    return false;
  }
  const responseData = error.response?.data;
  return isApiErrorResponse(responseData) && responseData.detail.toLowerCase().includes('csrf');
};

/**
 * Determine if token refresh should be attempted
 */
const shouldTryTokenRefresh = (
  error: AxiosError,
  originalRequest: ExtendedAxiosRequestConfig | undefined,
  isAuth: boolean,
  isExport: boolean,
): boolean => {
  if (!originalRequest || originalRequest._retry || isAuth) {
    return false;
  }

  const status = error.response?.status;
  const csrfError = isCsrfError(error);

  // Allow token refresh for CSRF errors on export endpoints (fresh token will have new CSRF token)
  if (csrfError) {
    authLogger.debug('CSRF error detected, allowing token refresh even on export endpoint', {
      status,
      isExport,
    });
    return true;
  }

  // For non-CSRF errors, skip export endpoints
  if (isExport) {
    return false;
  }

  const isAuthError = status === 401;

  // For 403, only retry if it's a CSRF error
  if (status === 403 && !csrfError) {
    return false;
  }

  // Refresh only for clear auth signals:
  // - 401 unauthorized
  // - CSRF-specific 403
  // Never on generic 5xx business endpoint failures.
  return (isAuthError || csrfError) && !!originalRequest;
};

/**
 * Attempt to refresh token and retry request
 */
const attemptTokenRefresh = async (
  error: AxiosError,
  originalRequest: ExtendedAxiosRequestConfig,
  apiClient: AxiosInstance,
): Promise<AxiosResponse | void> => {
  originalRequest._retry = true;
  originalRequest.useCache = false; // Don't use cache for retry

  try {
    authLogger.info(`${error.response?.status} error detected, attempting token refresh`);
    await refreshAccessToken();
    authLogger.info('Retrying original request with refreshed token (in cookie)');
    return apiClient(originalRequest);
  } catch (refreshError) {
    authLogger.error('Token refresh failed in apiClient interceptor:', refreshError);
    clearServiceWorkerCache();
    triggerAuthError();
  }
};

/**
 * Handle persistent auth errors after refresh attempt
 */
const handlePersistentAuthError = (
  error: AxiosError,
  originalRequest: ExtendedAxiosRequestConfig | undefined,
  isExport: boolean,
): void => {
  const url = originalRequest?.url || 'unknown endpoint';

  if (isUnauthorizedError(error) && originalRequest?._retry && !isExport) {
    handleAuthError({
      error,
      context: `persistent error at ${url}`,
    });
  } else if (isForbiddenError(error)) {
    uiLogger.warn('Forbidden request - insufficient permissions. Keeping session active.', {
      url,
    });
  }
};

/** Backend error code when user has no base plan (B2C or B2B) */
const BASE_PLAN_REQUIRED_CODE = 'BASE_PLAN_REQUIRED';
const REDIRECTED_NO_PLAN_QUERY = 'redirectedNoPlan=1';

/**
 * If the error is 403 with code BASE_PLAN_REQUIRED, redirect to the pricing page
 * (or enterprise billing for org users) so the user can choose a plan. Shows a toast
 * on the destination page via query param. Returns true if redirect was performed.
 */
const handleBasePlanRequired = async (
  error: AxiosError,
  _apiClient: AxiosInstance,
): Promise<boolean> => {
  if (error.response?.status !== 403) return false;
  const data = error.response?.data as { detail?: { code?: string; is_b2b?: boolean } } | undefined;
  const detail = data?.detail;
  if (typeof detail !== 'object' || detail?.code !== BASE_PLAN_REQUIRED_CODE) return false;

  const isB2B = detail.is_b2b === true;
  const path = isB2B ? PROFILE_ENTERPRISE_BILLING_PATH : '/profile/pricing';
  const url = `${path}?${REDIRECTED_NO_PLAN_QUERY}`;
  apiLogger.info('Base plan required – redirecting to pricing', { isB2B, path });
  window.location.href = url;
  return true;
};

/**
 * Handle CSRF token errors
 */
const handleCsrfError = (error: AxiosError): void => {
  if (isCsrfError(error)) {
    authLogger.warn('CSRF token validation failed - token may be missing or invalid');
    return;
  }

  if (error.response?.status === 403) {
    authLogger.warn('Forbidden request - insufficient permissions');
  }
};

/**
 * Track failed API performance
 */
const trackFailedApiPerformance = (error: AxiosError): void => {
  const config = error.config as ExtendedAxiosRequestConfig;
  if (config?._startTime && config.url && config.method) {
    const duration = performance.now() - config._startTime;
    const endpoint = config.url.replace(AUTH_CONFIG.API_BASE_URL || '', '');
    performanceMonitor.trackApiCall(endpoint, config.method.toUpperCase(), duration, false);
  }
};

/**
 * Create a centralized API client instance with consistent configuration
 * This eliminates duplication across apiService.ts, api.ts, and googleService.ts
 */
export const createApiClient = (config: AxiosRequestConfig = {}): ExtendedAxiosInstance => {
  const apiClient = axios.create({
    baseURL: AUTH_CONFIG.API_BASE_URL,
    timeout: 900000, // 15 min — large document / analysis requests; streams use HEARTBEAT_TIMEOUT_MS
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable HTTP-only cookies for secure token storage
    ...config,
  });

  /**
   * Get CSRF token from cookie with fallback to in-memory registry
   */
  const getCsrfToken = (): string | null => {
    // 1. Try reading from cookie using shared utility
    const token = getCsrfTokenFromDocumentCookie(authLogger);
    if (token) {
      return token;
    }

    // 2. Fallback to in-memory registry (common for cross-origin or initial load)
    const registryToken = getCsrfTokenFromRegistry();
    if (registryToken) {
      authLogger.debug('CSRF token retrieved from in-memory registry fallback');
      return registryToken;
    }

    authLogger.debug('CSRF token not found in cookie and no registry fallback');
    return null;
  };

  // Request interceptor for performance tracking and CSRF token
  // Note: Tokens are now stored in HTTP-only cookies and sent automatically
  apiClient.interceptors.request.use(
    (config) => {
      // Add performance tracking
      (config as ExtendedAxiosRequestConfig)._startTime = performance.now();

      // Special handling for new session endpoint to provide better logging for heavy requests
      if (config.url === CHAT_ENDPOINTS.NEW_SESSION) {
        apiLogger.info('Initiating new chat session creation... This may take a moment.');
      }

      // Add CSRF token to headers for state-changing requests
      // CSRF protection is required when using HTTP-only cookies
      const method = config.method?.toLowerCase() || '';

      if (['post', 'put', 'patch', 'delete'].includes(method)) {
        const csrfToken = getCsrfToken();

        if (csrfToken) {
          config.headers = config.headers || {};
          config.headers['X-CSRF-Token'] = csrfToken;
        } else {
          authLogger.warn('CSRF token not found in cookie for state-changing request');
        }
      }

      return config;
    },
    (error) => {
      authLogger.error('Request interceptor error:', error);
      return Promise.reject(new Error(error.message || 'Request interceptor error'));
    },
  );

  // Response interceptor for error handling, automatic token refresh, and performance tracking
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      // Track API performance
      const config = response.config as ExtendedAxiosRequestConfig;
      if (config._startTime && config.url && config.method) {
        const duration = performance.now() - config._startTime;
        const endpoint = config.url.replace(AUTH_CONFIG.API_BASE_URL || '', '');
        performanceMonitor.trackApiCall(endpoint, config.method.toUpperCase(), duration, true);
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;
      const url = originalRequest?.url;
      const isAuth = isAuthEndpoint(url);
      const isExport = isExportEndpoint(url);

      // Attempt token refresh if conditions are met
      if (shouldTryTokenRefresh(error, originalRequest, isAuth, isExport) && originalRequest) {
        const result = await attemptTokenRefresh(error, originalRequest, apiClient);
        if (result) {
          return result;
        }
      }

      // Redirect to Starter plan Stripe checkout when backend requires a base plan
      if (await handleBasePlanRequired(error, apiClient)) {
        throw error;
      }

      // Handle persistent auth errors after refresh attempt
      handlePersistentAuthError(error, originalRequest, isExport);

      // Handle CSRF token errors
      handleCsrfError(error);

      // Track failed API performance
      trackFailedApiPerformance(error);

      attachSupportRequestIdToAxiosError(error);

      // Return the original error to preserve response.data.detail
      throw error;
    },
  );

  // Setup security interceptors for comprehensive security event tracking
  setupSecurityInterceptors(apiClient);

  return apiClient as ExtendedAxiosInstance;
};

// Export a default API client instance for backward compatibility
export const apiClient = createApiClient();

// Export utility functions for token management
export const getAuthToken = getAccessToken;
